
import { Loader } from '@googlemaps/js-api-loader'
import Breadcrumb from '@/components/Breadcrumb'
import { buildOgLocaleMeta } from '@/utils/seo'
import {
  NEAR_RADIUS_KM,
  centroid,
  countByType,
  countNear,
  directoryGroups,
  formatKm,
  locateAmongPartners,
  rankPartners,
  schemaType,
  withTypes,
} from '@/utils/partnerLocator'

const GEOCODER_TIMEOUT_MS = 5000

export default {
  name: 'Partenaires',
  components: {
    Breadcrumb,
  },
  data() {
    return {
      breadcrumb_list: [
        {
          text: this.$t('pages.quiSommesNousPartenaires.breadcrumbHome'),
          to: { name: 'index' },
        },
        {
          text: this.$t('pages.quiSommesNousPartenaires.breadcrumbOurPartners'),
          to: { name: 'qui-sommes-nous-partenaires' },
        },
      ],
      rawPartners: [],
      types: [],
      query: '',
      origin: null,
      typeId: null,
      selectedId: null,
      hoverId: null,
      mobileView: 'list',
      locating: false,
      error: null,
      limit: 20,
    }
  },
  async fetch() {
    const { data } = await this.$partnerService.fetchDirectory()
    this.types = data.types || []
    this.rawPartners = data.partners || []
    const requested = this.types.find(
      type => type.identifier === this.$route.query.type
    )
    this.typeId =
      requested && this.counts[requested.id] > 0 ? requested.id : null
  },
  head() {
    const canonicalUrl =
      this.$config.url +
      this.localePath({ name: 'qui-sommes-nous-partenaires' })
    const metaTitle = this.$t('pages.quiSommesNousPartenaires.metaTitle')
    const metaDescription = this.$t(
      'pages.quiSommesNousPartenaires.metaDescription'
    )
    return {
      title: metaTitle,
      link: [{ rel: 'canonical', hid: 'canonical', href: canonicalUrl }],
      meta: [
        { hid: 'description', name: 'description', content: metaDescription },
        { hid: 'og:type', property: 'og:type', content: 'website' },
        { hid: 'og:url', property: 'og:url', content: canonicalUrl },
        { hid: 'og:title', property: 'og:title', content: metaTitle },
        {
          hid: 'og:description',
          property: 'og:description',
          content: metaDescription,
        },
        {
          hid: 'og:site_name',
          property: 'og:site_name',
          content: 'AGM Vision',
        },
        ...buildOgLocaleMeta(this.$i18n.locale),
        {
          hid: 'og:image',
          property: 'og:image',
          content: `${this.$config.url}/logo_og.png`,
        },
        {
          hid: 'twitter:card',
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        { hid: 'twitter:title', name: 'twitter:title', content: metaTitle },
        {
          hid: 'twitter:description',
          name: 'twitter:description',
          content: metaDescription,
        },
        {
          hid: 'twitter:image',
          name: 'twitter:image',
          content: `${this.$config.url}/logo_og.png`,
        },
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [
        {
          hid: 'ld-json-breadcrumb',
          type: 'application/ld+json',
          innerHTML: JSON.stringify(this.breadcrumbSchema),
        },
        {
          hid: 'ld-json-itemlist',
          type: 'application/ld+json',
          innerHTML: JSON.stringify(this.partnersSchema),
        },
      ],
    }
  },
  computed: {
    partners() {
      return withTypes(this.rawPartners, this.types)
    },
    counts() {
      return countByType(this.partners, this.types)
    },
    offeredTypes() {
      return this.types.filter(type => this.counts[type.id] > 0)
    },
    activeType() {
      return this.types.find(type => type.id === this.typeId) || null
    },
    results() {
      return rankPartners(this.partners, {
        typeId: this.typeId,
        origin: this.origin,
      })
    },
    nearest() {
      return this.origin && this.results.length ? this.results[0] : null
    },
    rest() {
      return this.nearest ? this.results.slice(1) : this.results
    },
    shown() {
      return this.rest.slice(0, this.limit)
    },
    groups() {
      return directoryGroups(
        this.typeId
          ? this.partners.filter(partner =>
              partner.typeIds.includes(this.typeId)
            )
          : this.partners
      )
    },
    fitIds() {
      return this.origin ? this.results.slice(0, 5).map(p => p.id) : null
    },
    summary() {
      if (!this.origin) {
        return this.$tc(
          'pages.quiSommesNousPartenaires.partnerCount',
          this.results.length,
          { count: this.results.length }
        )
      }
      const near = countNear(this.results, NEAR_RADIUS_KM)
      if (!near && this.results.length) {
        return this.$t('pages.quiSommesNousPartenaires.summaryNoneNear', {
          distance: formatKm(this.results[0].distance, this.$i18n.locale),
        })
      }
      return this.$tc('pages.quiSommesNousPartenaires.summaryNear', near, {
        count: near,
        place: this.origin.label,
      })
    },
    breadcrumbSchema() {
      const homeUrl = this.$config.url + this.localePath({ name: 'index' })
      const aboutUrl =
        this.$config.url + this.localePath({ name: 'magasin-excideuil' })
      const partnersUrl =
        this.$config.url +
        this.localePath({ name: 'qui-sommes-nous-partenaires' })
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: this.$t('pages.quiSommesNousPartenaires.breadcrumbHome'),
            item: homeUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: this.$t('pages.magasinExcideuil.breadcrumbOurStore'),
            item: aboutUrl,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: this.$t(
              'pages.quiSommesNousPartenaires.breadcrumbOurPartners'
            ),
            item: partnersUrl,
          },
        ],
      }
    },
    partnersSchema() {
      const partnersUrl =
        this.$config.url +
        this.localePath({ name: 'qui-sommes-nous-partenaires' })
      const partners = rankPartners(this.partners)
      return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: this.$t('pages.quiSommesNousPartenaires.metaTitle'),
        description: this.$t('pages.quiSommesNousPartenaires.metaDescription'),
        url: partnersUrl,
        numberOfItems: partners.length,
        itemListElement: partners.map((partner, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': schemaType(partner),
            name: partner.company,
            ...(partner.image?.url && { image: partner.image.url }),
            ...(partner.website && { url: partner.website }),
            ...(partner.phone && { telephone: partner.phone }),
            ...(partner.types.length && {
              knowsAbout: partner.types.map(type => type.name),
            }),
            address: {
              '@type': 'PostalAddress',
              ...(partner.address && { streetAddress: partner.address }),
              ...(partner.city && { addressLocality: partner.city }),
              ...(partner.postcode && { postalCode: partner.postcode }),
              ...(partner.country?.iso && {
                addressCountry: partner.country.iso.toUpperCase(),
              }),
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: partner.lat,
              longitude: partner.lng,
            },
          },
        })),
      }
    },
  },
  watch: {
    typeId() {
      this.limit = 20
      this.selectedId = null
    },
  },
  methods: {
    toggleType(id) {
      this.typeId = this.typeId === id ? null : id
    },
    async geocode(query) {
      const loader = new Loader({
        apiKey: this.$config.googleMapsApiKey,
        version: 'weekly',
      })
      const lookup = loader.load().then(async ({ maps }) => {
        const { Geocoder } = await maps.importLibrary('geocoding')
        const { results } = await new Geocoder().geocode({
          address: query,
          region: this.$i18n.locale === 'fr' ? 'fr' : undefined,
        })
        if (!results || !results.length) return null
        const place = results[0]
        const locality = place.address_components.find(component =>
          component.types.includes('locality')
        )
        return {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          label: locality ? locality.long_name : query.trim(),
        }
      })
      const timeout = new Promise(resolve =>
        setTimeout(() => resolve(null), GEOCODER_TIMEOUT_MS)
      )
      try {
        return await Promise.race([lookup, timeout])
      } catch (error) {
        return null
      }
    },
    async search() {
      if (!this.query.trim() || this.locating) return
      this.locating = true
      this.error = null
      const origin =
        (await this.geocode(this.query)) ||
        locateAmongPartners(this.query, this.partners)
      this.locating = false
      if (!origin) {
        this.error = this.$t('pages.quiSommesNousPartenaires.placeNotFound')
        return
      }
      this.setOrigin(origin)
    },
    aroundMe() {
      if (!process.client || !navigator.geolocation) {
        this.error = this.$t(
          'pages.quiSommesNousPartenaires.positionUnavailable'
        )
        return
      }
      this.locating = true
      this.error = null
      navigator.geolocation.getCurrentPosition(
        position => {
          this.locating = false
          this.query = ''
          this.setOrigin({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            label: this.$t('pages.quiSommesNousPartenaires.yourPosition'),
          })
        },
        () => {
          this.locating = false
          this.error = this.$t(
            'pages.quiSommesNousPartenaires.positionUnavailable'
          )
        },
        { timeout: 10000 }
      )
    },
    openGroup(group) {
      const point = centroid(group.partners)
      if (!point) return
      this.query = ''
      this.setOrigin({ ...point, label: group.label })
    },
    setOrigin(origin) {
      this.origin = origin
      this.limit = 20
      this.selectedId = null
      this.mobileView = 'list'
      this.$nextTick(() => {
        const target = this.$refs.results && this.$refs.results.$el
        if (target)
          target.scrollIntoView({ block: 'start', behavior: 'smooth' })
      })
    },
    clearOrigin() {
      this.origin = null
      this.query = ''
    },
    select(partner) {
      this.selectedId = this.selectedId === partner.id ? null : partner.id
      if (this.selectedId && this.$refs.map) this.$refs.map.focus(partner)
    },
    onMapSelect(partner) {
      if (!partner) return
      if (this.nearest && partner.id === this.nearest.id) {
        this.selectedId = null
      } else {
        const index = this.rest.findIndex(p => p.id === partner.id)
        if (index >= this.limit) this.limit = index + 1
        this.selectedId = partner.id
      }
      if (this.$device.isMobile) this.mobileView = 'list'
      this.$nextTick(() => {
        const ref = this.$refs[`partner-${partner.id}`]
        const component = Array.isArray(ref) ? ref[0] : ref
        const element = component && (component.$el || component)
        if (element)
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    },
  },
}
